import { BaseService } from "../BaseService";
import { AuthService } from "../auth/auth.service";
import { environment } from '../../../../src/environments/environment';
import { HttpClient } from "@angular/common/http";
import { Router } from '@angular/router';
import { Observable, of, throwError } from "rxjs";
import { catchError, concatMap, delay, map, retryWhen, take } from 'rxjs/operators';
import { ApiResult } from "../../models/ApiResult";
import { SiteConfigModel } from "../../models/api/SiteConfigModel";
import { SiteConfigFindResultModel } from "../../models/api/SiteConfigFindResultModel";

export abstract class SiteConfigBaseService extends BaseService {

    private urlPart: string = 'SiteConfig';

    constructor(private http: HttpClient, private router: Router, private auth: AuthService) {
        super(http, router, auth);
        this.apiSegmentUrl = this.urlPart;
    };

    /// <summary>
    /// FIND: Retrieves a list of SiteConfig records from the database
    /// /SiteConfig
    /// </summary>
    public findSiteConfig(page: number = 0, size: number = 0, orderBy: string = "", orderDirection: string = "ASC", filter: string = ""): Observable<SiteConfigFindResultModel>
    {
        // Find SiteConfig Records

        const url = `${super.getBaseUrl()}?page=${page}&size=${size}&orderBy=${orderBy}&orderDirection=${orderDirection}&filter=${filter}`;

        if (!environment.production)
            console.log("Find SiteConfig: Calling " + url);

        return this.http.get<SiteConfigFindResultModel>(url)
            .pipe(
                map(next => {
                    let o: SiteConfigFindResultModel = Object.assign(new SiteConfigFindResultModel(), next);
                    return o;
                }),
                retryWhen(errors =>
                    errors.pipe(
                        concatMap((error, count) => {
                            if (count > this.RETRY_CEILING) return throwError(error);
                            return this.tokenCheckAndRefresh(error);
                        }),
                        delay(1000),
                    ),
                ),
                catchError(this.errorHandler)
            );
    }

    /// <summary>
    /// GET: Retrieves a single SiteConfig record from the database
    /// /SiteConfig/{siteconfigid}
    /// </summary>
    /// <param name="siteconfigid"></param>
    /// <returns></returns>
    public getSiteConfig(siteconfigid: number): Observable<SiteConfigModel>
    {

        // Get Single SiteConfig Record

        // TODO: Get Single SiteConfig Record

        const url = `${super.getBaseUrl()}/${siteconfigid}`;

        if (!environment.production)
            console.log("Get SiteConfig: Calling " + url);

        return this.http.get<SiteConfigModel>(url)
            .pipe(
                map(next => {
                    let o: SiteConfigModel = Object.assign(new SiteConfigModel(), next);
                    return o;
                }),
                retryWhen(errors =>
                    errors.pipe(
                        concatMap((error, count) => {
                            if (count > this.RETRY_CEILING) return throwError(error);
                            return this.tokenCheckAndRefresh(error);
                        }),
                        delay(1000),
                    ),
                ),
                catchError(this.errorHandler)
            );
    }

    /// <summary>
    /// POST: Adds a new SiteConfig record to the database
    /// /SiteConfig/{siteconfigid}
    /// </summary>
    /// <param name="siteconfigid"></param>
    /// <returns></returns>
    public createSiteConfig(newRecord: SiteConfigModel): Observable<SiteConfigModel>
    {
        // Add Single SiteConfig Record

        const url = `${super.getBaseUrl()}`;

        if (!environment.production)
            console.log("Create SiteConfig:Calling " + url);

        return this.http.post<SiteConfigModel>(url, newRecord)
            .pipe(
                map(next => {
                    let o: SiteConfigModel = Object.assign(new SiteConfigModel(), next);
                    return o;
                }),
                retryWhen(errors =>
                    errors.pipe(
                        concatMap((error, count) => {
                            if (count > this.RETRY_CEILING) return throwError(error);
                            return this.tokenCheckAndRefresh(error);
                        }),
                        delay(1000),
                    ),
                ),
                catchError(this.errorHandler)
            );
    }

    /// <summary>
    /// PUT: Updates an existing SiteConfig record in the database
    /// /SiteConfig/{siteconfigid}
    /// </summary>
    /// <param name="siteconfigid"></param>
    /// <returns></returns>
    public updateSiteConfig(siteconfigid: number, updatedRecord: SiteConfigModel): Observable<SiteConfigModel>
    {
        // Update Single SiteConfig Record

        const url = `${super.getBaseUrl()}/${siteconfigid}`;

        if (!environment.production)
            console.log("Update SiteConfig: Calling " + url);

        return this.http.put<SiteConfigModel>(url, updatedRecord)
            .pipe(
                map(next => {
                    let o: SiteConfigModel = Object.assign(new SiteConfigModel(), next);
                    return o;
                }),
                retryWhen(errors =>
                    errors.pipe(
                        concatMap((error, count) => {
                            if (count > this.RETRY_CEILING) return throwError(error);
                            return this.tokenCheckAndRefresh(error);
                        }),
                        delay(1000),
                    ),
                ),
                catchError(this.errorHandler)
            );
    }

    public updateSiteConfigRecord(updatedRecord: SiteConfigModel): Observable<SiteConfigModel>
    {
        return this.updateSiteConfig(updatedRecord.siteConfigID, updatedRecord);
    }

    /// <summary>
    /// DELETE: Deletes an existing SiteConfig record from the database
    /// /SiteConfig/{siteconfigid}
    /// </summary>
    /// <param name="siteconfigid"></param>
    /// <returns></returns>
    public deleteSiteConfig(siteconfigid: number): Observable<ApiResult>
    {
        // Delete Single SiteConfig Record

        const url = `${super.getBaseUrl()}/${siteconfigid}`;

        if (!environment.production)
            console.log("Delete SiteConfig: Calling " + url);

        return this.http.delete<ApiResult>(url)
            .pipe(
                map(next => {
                    let o: ApiResult = new ApiResult();
                    o.success = true;
                    return o;
                }),
                retryWhen(errors =>
                    errors.pipe(
                        concatMap((error, count) => {
                            if (count > this.RETRY_CEILING) return throwError(error);
                            return this.tokenCheckAndRefresh(error);
                        }),
                        delay(1000),
                    ),
                ),
                catchError(this.errorHandler)
            );
    }

    public deleteSiteConfigRecord(deleteRecord: SiteConfigModel): Observable<ApiResult>
    {
        // Delete Single SiteConfig Record
        return this.deleteSiteConfig(deleteRecord.siteConfigID);
    }

}
